import { useEffect, useState } from 'react';
import { isNil } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import { EmailInput } from '@paid-ui/components/email-input';
import { FormControl, FormField, FormHelper, FormItem } from '@paid-ui/components/form';
import { useUserEmailCheck } from '@paid-ui/queries/user-email-check';

import { signUpManager } from '../../models';
import { SignUpStep } from '../../models/enums';

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep) => void;
}

export const StepChangeEmail: React.FC<Props> = ({ disabled, goToStep }) => {
  const form = useFormContext();
  const { currentStep } = useSnapshot(signUpManager);
  const [email, setEmail] = useState('');
  const { data, refetch, isFetching } = useUserEmailCheck({ email });
  const _email = form.watch('email');
  const isValid = Boolean(_email) && !form.getFieldState('email').invalid;

  const handleNext = () => {
    setEmail(form.getValues('email'));
    setTimeout(refetch, 100);
  };

  useEffect(() => {
    if (isNil(data)) {
      return;
    }

    if (data) {
      form.setFocus('email');
      form.setError('email', {
        type: 'manual',
        message: 'The email has been registered, please try another.',
      });
    } else {
      form.trigger('email');
      goToStep?.(SignUpStep.ONE_TIME_CODE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching]);

  if (currentStep !== SignUpStep.CHANGE_EMAIL) {
    return null;
  }

  return (
    <div className="space-y-9">
      <FormField
        name="email"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <EmailInput
                {...field}
                placeholder="Enter your email"
                autoComplete="off"
                disabled={disabled}
              />
            </FormControl>
            <FormHelper />
          </FormItem>
        )}
      />
      <Button
        variant="solid"
        className="h-[44px]"
        disabled={disabled || !isValid}
        loading={isFetching}
        onClick={handleNext}
      >
        {isFetching ? 'Checking...' : 'Continue'}
      </Button>
    </div>
  );
};
